import { ConfigService } from '@config/config.service';
import { ProjectsActions, ProjectsActionTypes } from '@crm/ngrx/actions/projects.actions';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { ProjectResponse } from '@services/associations/model/projectResponse';
import { ProjectSearchRequest } from '@services/associations/model/projectSearchRequest';
import { PaginationModel } from '@shared/models/pagination.model';

export const adapter: EntityAdapter<ProjectResponse> = createEntityAdapter<ProjectResponse>();

export interface State extends EntityState<ProjectResponse> {
  loading: boolean,
  pagination: PaginationModel,
  filter: ProjectSearchRequest,
  totalByPages: number | undefined,
}

export const initialState: State = adapter.getInitialState({
  loading: false,
  pagination: {
    first: true,
    last: false,
    number: 0,
    numberOfElements: 0,
    size: new ConfigService().paginationConfig.pageSize,
    totalElements: 0,
  },
  filter: {
    delayedOnly: false
  },
  totalByPages: 0,
});

export function reducer(state = initialState, action: ProjectsActions): State {
  switch (action.type) {
    /** Load Projects */
    case ProjectsActionTypes.LoadProjects:
      return {...state, loading: !action.payload?.dontShowLoading};

    case ProjectsActionTypes.LoadProjectsSuccess:
      return adapter.setAll(action.payload.content!, {
        ...state,
        loading: false,
        pagination: {
          size: action.payload.size,
          last: action.payload.last,
          totalElements: action.payload.totalElements,
          numberOfElements: action.payload.numberOfElements,
          first: action.payload.first,
          number: action.payload.number,
        },
        totalByPages: action.payload.totalByPages,
      });

    case ProjectsActionTypes.LoadProjectsFailure:
      return {...state, loading: false};

    /** Edit Project */
    case ProjectsActionTypes.EditProjects:
      return {...state};

    case ProjectsActionTypes.EditProjectsSuccess:
      return adapter.updateOne({
        id: action.payload.id!,
        changes: action.payload,
      }, {...state});

    case ProjectsActionTypes.EditProjectsFailure:
      return {...state};

    /** Change Page */
    case ProjectsActionTypes.ChangePage:
      return {
        ...state,
        pagination: {
          ...state.pagination,
          number: action.payload.number,
          size: action.payload.size,
        }
      };

    /** Set Initial Pagination */
    case ProjectsActionTypes.SetInitialPagination:
      return {...state, pagination: initialState.pagination};

    /** Set Filter */
    case ProjectsActionTypes.SetFilter:
      return {...state, filter: action.payload};

    /** Clear Filter */
    case ProjectsActionTypes.ClearFilter:
      return {...state, filter: initialState.filter};

    /** Set Initial State */
    case ProjectsActionTypes.SetInitialState:
      return {...initialState};

    default:
      return state;
  }
}

export const {
  selectIds: selectProjectsIds,
  selectEntities: selectProjectsEntities,
  selectAll: selectAllProjects,
  selectTotal: selectProjectsTotal,
} = adapter.getSelectors();
